import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../theme';

const Header: React.FC = () => {
    const { user, logout, isAdmin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleNavClick = (path: string) => {
        navigate(path);
        setMenuOpen(false);
    };

    const isActive = (path: string) => location.pathname === path;

    return (
        <header style={styles.header}>
            <div style={styles.headerTop}>
                <div style={styles.logo} onClick={() => handleNavClick('/')}>
                    <span style={styles.logoText}>Bøkelunden Sameie</span>
                </div>
                
                <button 
                    className="menu-button"
                    style={styles.menuButton} 
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label="Meny"
                >
                    <span style={styles.menuIcon}>{menuOpen ? '✕' : '☰'}</span>
                </button>

                <nav className="desktop-nav" style={styles.desktopNav}>
                    <button 
                        style={{...styles.navButton, ...(isActive('/') ? styles.navButtonActive : {})}} 
                        onClick={() => handleNavClick('/')}
                    >
                        Oppslagstavle
                    </button>
                    <button 
                        style={{...styles.navButton, ...(isActive('/dokumenter') ? styles.navButtonActive : {})}} 
                        onClick={() => handleNavClick('/dokumenter')}
                    >
                        Dokumenter
                    </button>
                    <button 
                        style={{...styles.navButton, ...(isActive('/kontakter') ? styles.navButtonActive : {})}} 
                        onClick={() => handleNavClick('/kontakter')}
                    >
                        Kontakter
                    </button>
                    {isAdmin && (
                        <button 
                            style={{...styles.navButton, ...(isActive('/admin') ? styles.navButtonActive : {})}} 
                            onClick={() => handleNavClick('/admin')}
                        >
                            Admin
                        </button>
                    )}
                </nav>

                <div className="desktop-user-section" style={styles.desktopUserSection}>
                    <span style={styles.userName}>
                        {user?.fullName}
                        {user?.role && user.role !== 'Beboer' && (
                            <span style={styles.badge}>
                                {user.role === 'Owner' ? 'Admin' : 'Styret'}
                            </span>
                        )}
                    </span>
                    <button style={styles.logoutButton} onClick={handleLogout}>
                        Logg ut
                    </button>
                </div>
            </div>

            {menuOpen && (
                <div className="mobile-menu" style={styles.mobileMenu}>
                    <nav style={styles.mobileNav}>
                        <button 
                            style={{...styles.mobileNavButton, ...(isActive('/') ? styles.mobileNavButtonActive : {})}} 
                            onClick={() => handleNavClick('/')}
                        >
                            📋 Oppslagstavle
                        </button>
                        <button 
                            style={{...styles.mobileNavButton, ...(isActive('/dokumenter') ? styles.mobileNavButtonActive : {})}} 
                            onClick={() => handleNavClick('/dokumenter')}
                        >
                            📁 Dokumenter
                        </button>
                        <button 
                            style={{...styles.mobileNavButton, ...(isActive('/kontakter') ? styles.mobileNavButtonActive : {})}} 
                            onClick={() => handleNavClick('/kontakter')}
                        >
                            📞 Kontakter
                        </button>
                        {isAdmin && (
                            <button 
                                style={{...styles.mobileNavButton, ...(isActive('/admin') ? styles.mobileNavButtonActive : {})}} 
                                onClick={() => handleNavClick('/admin')}
                            >
                                ⚙️ Admin
                            </button>
                        )}
                    </nav>
                    <div style={styles.mobileUserSection}>
                        <div style={styles.mobileUserInfo}>
                            <span style={styles.mobileUserName}>{user?.fullName}</span>
                            {user?.role && user.role !== 'Beboer' && (
                                <span style={styles.badge}>
                                    {user.role === 'Owner' ? 'Admin' : 'Styret'}
                                </span>
                            )}
                        </div>
                        <button style={styles.mobileLogoutButton} onClick={handleLogout}>
                            Logg ut
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    header: {
        backgroundColor: theme.primaryColor,
        color: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
    },
    headerTop: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
    },
    logo: {
        cursor: 'pointer',
    },
    logoText: {
        fontSize: '18px',
        fontWeight: 'bold',
    },
    menuButton: {
        display: 'none',
        padding: '8px',
        backgroundColor: 'transparent',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        fontSize: '24px',
    },
    menuIcon: {
        display: 'block',
        lineHeight: 1,
    },
    desktopNav: {
        display: 'flex',
        gap: '8px',
    },
    navButton: {
        padding: '8px 14px',
        backgroundColor: 'rgba(255,255,255,0.1)',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'background-color 0.2s',
    },
    navButtonActive: {
        backgroundColor: 'rgba(255,255,255,0.25)',
    },
    desktopUserSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    userName: {
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    badge: {
        backgroundColor: 'rgba(255,255,255,0.2)',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '12px',
    },
    logoutButton: {
        padding: '8px 16px',
        backgroundColor: 'transparent',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.5)',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
    },
    mobileMenu: {
        display: 'none',
        borderTop: '1px solid rgba(255,255,255,0.2)',
        padding: '16px',
    },
    mobileNav: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    mobileNavButton: {
        padding: '14px 16px',
        backgroundColor: 'rgba(255,255,255,0.1)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '16px',
        textAlign: 'left',
    },
    mobileNavButtonActive: {
        backgroundColor: 'rgba(255,255,255,0.25)',
    },
    mobileUserSection: {
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: '1px solid rgba(255,255,255,0.2)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    mobileUserInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    mobileUserName: {
        fontSize: '14px',
    },
    mobileLogoutButton: {
        padding: '10px 20px',
        backgroundColor: 'rgba(255,255,255,0.15)',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
    },
};

export default Header;
